import React, { useEffect, useState } from "react";
import { useAutoRefresh } from "@/lib/autoRefresh";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Banknote, Inbox, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DisburseDialog, BulkDisburseDialog } from "@/components/FundSource";
import { Checkbox } from "@/components/ui/checkbox";

export default function Disbursement() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);

  const load = () => api.get("/cash-requests", { params: { status: "approved" } })
    .then((r) => { setRows(r.data); setSel((s) => s.filter((id) => r.data.some((x) => x.id === id))); }).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = rows.length > 0 && sel.length === rows.length;
  const picked = rows.filter((r) => sel.includes(r.id));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Banknote className="h-6 w-6 text-brand" /> Pencairan Kas
          </h2>
          <p className="text-sm text-muted-foreground">Pengajuan yang telah disetujui dan menunggu pencairan oleh Tim Keuangan.</p>
        </div>
        {rows.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="disburse-select-all">
              <Checkbox checked={allOn} onCheckedChange={() => setSel(allOn ? [] : rows.map((r) => r.id))} /> Pilih semua
            </label>
            <BulkDisburseDialog reqs={picked} onDone={() => { setSel([]); load(); }} />
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <Card className="p-12 flex flex-col items-center justify-center text-center">
          <Inbox className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="font-medium">Tidak ada kas menunggu pencairan</p>
          <p className="text-sm text-muted-foreground">Semua pengajuan yang disetujui sudah dicairkan.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <Card key={r.id} className={`p-5 ${sel.includes(r.id) ? "ring-2 ring-brand/50" : ""}`} data-testid={`disburse-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  <Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} className="mt-1" data-testid={`disburse-check-${r.id}`} />
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{r.requester_name}</p>
                    <p className="text-xs text-muted-foreground">{r.divisi} · {formatDate(r.created_at)}</p>
                  </div>
                </div>
                <span className="text-lg font-bold font-mono text-brand shrink-0">{formatRupiah(r.approved_amount)}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{r.reason}</p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate(`/requests/${r.id}`)} className="flex-1" data-testid={`detail-btn-${r.id}`}>
                  Detail <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <DisburseDialog req={r} onDone={load} trigger={
                  <Button className="flex-1 bg-brand hover:bg-emerald-800" data-testid={`disburse-btn-${r.id}`}>
                    <Banknote className="h-4 w-4 mr-1.5" /> Cairkan
                  </Button>
                } />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
